import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm, SubmitHandler } from "react-hook-form";
import {
  createSubCategory,
  findAllCategory,
  findSubCategoryById,
  updateSubCategoryById,
} from "../../../../api/inventoryService";
import CloseIcon from "@mui/icons-material/Close";
import CategoryIcon from "@mui/icons-material/Category";

interface AddSubCategoryProps {
  closeModal: () => void;
  id?: string;
}

interface CategoryOption {
  _id: string;
  categoryName: string;
}

interface FormData {
  subCategoryName: string;
  categoryId: string;
}

const AddSubCategory: React.FC<AddSubCategoryProps> = ({ closeModal, id }) => {
  const methods = useForm<FormData>();
  const [categories, setCategories] = useState<CategoryOption[]>([]);

  useEffect(() => {
    findAllCategory()
      .then((data) => setCategories(data as CategoryOption[]))
      .catch((error) => console.error(error?.response));
  }, []);

  const getSubCategoryData = useCallback(
    (subCategoryId: string) => {
      findSubCategoryById(subCategoryId)
        .then((data: any) => {
          methods.reset({
            subCategoryName: data.subCategoryName,
            categoryId: data.categoryId?._id ?? data.categoryId,
          });
        })
        .catch((error) => {
          console.error(error?.response);
        });
    },
    [methods]
  );

  useEffect(() => {
    if (id) {
      getSubCategoryData(id);
    }
  }, [id, getSubCategoryData]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    (data) => {
      const apiCall = id
        ? updateSubCategoryById(id, data)
        : createSubCategory(data);

      apiCall
        .then(() => closeModal())
        .catch((error) => console.error(error?.response));
    },
    [closeModal, id]
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-100 dark:bg-emerald-900/40 rounded-md flex items-center justify-center">
              <CategoryIcon className="text-emerald-600 dark:text-emerald-300" sx={{ fontSize: 14 }} />
            </div>
            <h2 className="text-xs font-bold text-slate-800 dark:text-slate-100">
              {id ? "Edit Sub Category" : "Add Sub Category"}
            </h2>
          </div>
          <button
            onClick={closeModal}
            className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors"
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </button>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="p-3 space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  {...methods.register("categoryId", { required: true })}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200"
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.categoryName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Sub Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...methods.register("subCategoryName", { required: true })}
                  type="text"
                  placeholder="Enter sub category name"
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                />
              </div>
            </div>

            <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-600 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2.5 text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors font-medium text-xs"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all font-medium text-xs"
              >
                {id ? "Update" : "Submit"}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default AddSubCategory;
